import { useState } from 'react';
import { Button } from '../ui/button';
import EmailClassificationEditor, { classificationMeta } from './EmailClassificationEditor';
import { findPendingIdRequest } from './StatusCard';
import HelpMeButton from './HelpMeButton';
import { stripQuotedReply } from '../../lib/emailQuotes';

const monoDay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }).toUpperCase();
};

const longDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
};

const itemDate = (item) => item.sent_at || item.received_at || item.created_at;

const COLLAPSED_LENGTH = 300;
const isLongEmail = (body) => body && body.length > 400;

/**
 * EmailTimeline — the correspondence on a request, newest first by default.
 * Messages run full width (left/right alignment read as harder to scan and
 * meant every message was a different width) — instead, their messages and
 * ours are coloured differently (`bg-ground` vs `bg-inset`) so the sender is
 * still obvious at a glance, and it means every message's left edge lines
 * up, which in turn means the note column lines up cleanly too.
 *
 * Each bubble leads with an email-style header — who it's actually from
 * (the organisation's name, or "You"), their address and the date on the
 * line below, and, where there's one, the classification pill top-right —
 * rather than the generic "FROM THEM"/"FROM YOU" tag that used to be
 * buried in a small mono date line. Anything the system did on the user's
 * behalf is stated as a fact ("Handled automatically — your ID was sent on
 * 2 March."). The sender's own email client's quoted-original tail
 * ("On ... wrote: >...") is cut for display via `stripQuotedReply` — the
 * stored body is untouched.
 *
 * A persistent ~75/25 split runs the height of the thread: messages in the
 * big column, and — where a message needs explaining — a note in a fixed
 * band on the right, top-aligned with the message it's about (`lg` and up;
 * below that the note stacks directly under its message). The note is
 * positioned out of normal flow so a note taller than its message hangs
 * down over the space below rather than pushing later messages down the
 * page. This replaces the old generic sidebar cards — the explanation
 * always lives next to the specific message it's about.
 *
 * A top border plus a proper section heading and one-line explanation
 * (rather than a small eyebrow) mark this off from the overview above —
 * SARStats/StatusCard on SARDetail — so it reads as the detail behind
 * those numbers, not something floating on its own.
 */
// Box background/text per classificationMeta tone — same four tones as the
// status pills elsewhere, so an amber explainer means the same thing here
// as it does on a status chip.
const TONE_BOX = {
  needs: 'bg-amber-wash text-amber-ink',
  breach: 'bg-breach-wash text-breach-ink',
  done: 'bg-brand-wash text-brand-ink',
  held: 'bg-ground text-ink-70',
  neutral: 'bg-ground text-ink-70',
};

/**
 * Plain-English context for an incoming message, keyed off its AI
 * classification — what this actually means and, where there's a concrete
 * next step, what to do about it. id_request has its own dedicated action
 * box alongside the message (send/upload ID) rather than going through here.
 */
const explainerFor = (item, deadline) => {
  switch (item.ai_classification) {
    case 'acknowledgment': {
      const due = deadline?.initial_deadline;
      return {
        body: due
          ? `This confirms they've received your request. By law they must reply by ${longDate(due)} — one calendar month from when they got it.`
          : "This confirms they've received your request. By law they have one calendar month to reply.",
      };
    }
    case 'extension_request': {
      const due = deadline?.extended_deadline;
      return {
        body: due
          ? `They're asking for more time. UK GDPR allows up to two further months, but only for genuinely complex or high-volume requests — never open-ended. Your new deadline is ${longDate(due)}.`
          : "They're asking for more time. UK GDPR allows up to two further months, but only for genuinely complex or high-volume requests — never open-ended.",
      };
    }
    case 'refusal':
      return {
        body: "They're refusing to provide some or all of your information. This isn't always lawful — organisations need a specific legal reason to withhold personal data. If this doesn't seem right, ask a Precious Data admin to take a look.",
        showHelp: true,
      };
    case 'portal_link':
    case 'upload_link_request':
      return {
        body: "They want you to use an external website rather than replying directly. Check it's genuinely from them before entering anything on it — we can't see what happens once you're there.",
      };
    case 'clarification':
      return {
        body: "They need more detail from you before they can act on this. Read their message and reply with what they're asking for — this can pause their deadline until you do.",
      };
    default:
      return null;
  }
};

const EmailTimeline = ({
  emailThread, sar, deadline, onReply, onRefresh,
  hasIdDocuments, onSendId, onUploadId, sendingId,
}) => {
  const [sortNewestFirst, setSortNewestFirst] = useState(true);
  const [expandedEmails, setExpandedEmails] = useState({});

  if (!emailThread || emailThread.length === 0) return null;

  // Same pending-request logic StatusCard uses for its headline, so the
  // inline prompt below (on the actual message that asked) and that
  // headline never disagree about whether this is still outstanding.
  const { pendingIdRequest, idAlreadySent } = findPendingIdRequest(emailThread);
  const status = sar?.effective_status || sar?.status;
  const showIdPrompt = Boolean(pendingIdRequest) && !idAlreadySent && status !== 'complete';

  const toggleExpanded = (emailId) => {
    setExpandedEmails((prev) => ({ ...prev, [emailId]: !prev[emailId] }));
  };

  const items = [...emailThread].sort((a, b) => {
    const diff = new Date(itemDate(a)) - new Date(itemDate(b));
    return sortNewestFirst ? -diff : diff;
  });

  const latest = items.reduce((acc, e) => (
    !acc || new Date(itemDate(e)) > new Date(itemDate(acc)) ? e : acc
  ), null);
  const waitingOnThem = latest?.direction === 'outgoing' && !['complete', 'escalated_ico'].includes(status);

  const waitingBox = waitingOnThem && (
    <div
      className="rounded-lg border border-dashed border-rule px-5 py-4 text-[15px] leading-[1.6] text-ink-55"
      data-testid="correspondence-waiting"
    >
      Nothing since. We check the inbox every few minutes and will tell you the moment they reply.
    </div>
  );

  /** Sender identity + one plain sentence for an entry. */
  const describe = (item) => {
    const isOutgoing = item.direction === 'outgoing';
    const isSarSent = item.type === 'sar_sent';
    const meta = classificationMeta(item.ai_classification);

    let summary;
    if (isSarSent) {
      summary = `Your request for a copy of everything ${sar?.organisation_name || 'they'} hold about you.`;
    } else if (isOutgoing && item.reminder_type) {
      summary = `Handled automatically — a reminder was sent on ${longDate(itemDate(item))} because they had not replied.`;
    } else if (isOutgoing && item.id_documents_attached) {
      summary = `Handled automatically — your ID was sent on ${longDate(itemDate(item))}.`;
    } else if (isOutgoing) {
      summary = 'Your reply.';
    } else {
      summary = item.subject || 'An email from them, with no subject.';
    }

    const fact = !isOutgoing && item.ai_auto_responded
      ? 'Handled automatically — we replied for you.'
      : null;

    return { isOutgoing, isSarSent, meta, summary, fact };
  };

  return (
    <section data-testid="correspondence" className="border-t border-rule pt-7">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-[22px] font-semibold tracking-[-0.02em] text-ink">
            Correspondence
          </h2>
          <p className="mt-1 text-sm text-ink-55">
            The {items.length} message{items.length === 1 ? '' : 's'} behind the numbers above — read
            them if you want the detail.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSortNewestFirst((v) => !v)}
          data-testid="sort-toggle-btn"
          aria-label={sortNewestFirst ? 'Showing newest first. Switch to oldest first.' : 'Showing oldest first. Switch to newest first.'}
          className="shrink-0"
        >
          {sortNewestFirst ? 'Newest first' : 'Oldest first'}
        </Button>
      </div>

      <ol className="flex flex-col gap-3">
        {sortNewestFirst && waitingBox && <li className="flex justify-center">{waitingBox}</li>}

        {items.map((item) => {
          const d = describe(item);
          // Quoted "On ... wrote:" tails come from the sender's own email
          // client, not from them — cut for display only, never stored.
          const displayBody = stripQuotedReply(item.body);
          const isLong = isLongEmail(displayBody);
          const isExpanded = expandedEmails[item.id];
          const showEditor = !d.isOutgoing && !d.isSarSent && item.ai_classification;
          const explainer = !d.isOutgoing ? explainerFor(item, deadline) : null;
          const explainerTone = explainer ? TONE_BOX[d.meta?.tone] || TONE_BOX.neutral : null;
          const isPendingIdMessage = showIdPrompt && item.id === pendingIdRequest.id;
          const hasSideNote = !d.isOutgoing && (explainer || isPendingIdMessage);

          const metaTone = d.meta?.tone === 'breach' && !d.isOutgoing ? 'text-breach-ink' : 'text-ink-55';

          const bubble = (
            <div
              className={`w-full rounded-lg px-5 py-4 min-w-0 ${d.isOutgoing ? 'bg-inset' : 'bg-ground'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-[15px] font-semibold leading-snug text-ink">
                    {d.isOutgoing ? 'You' : (sar?.organisation_name || 'The organisation')}
                  </p>
                  <p className={`mt-0.5 font-mono text-[11.5px] tracking-[0.03em] ${metaTone}`}>
                    {!d.isOutgoing && <>{item.from_address} · </>}
                    <span className="uppercase tracking-[0.06em]">{monoDay(itemDate(item))}</span>
                  </p>
                </div>
                {showEditor && (
                  <div className="shrink-0">
                    <EmailClassificationEditor email={item} onClassificationChanged={() => onRefresh?.()} />
                  </div>
                )}
              </div>

              <p className="mt-2.5 text-[15px] leading-snug text-ink">{d.summary}</p>
              {d.fact && <p className="mt-1 text-sm text-ink-55">{d.fact}</p>}

              {displayBody && (
                <div className="mt-3 text-sm leading-[1.6] text-ink-70">
                  <p className="whitespace-pre-wrap">
                    {isLong && !isExpanded ? `${displayBody.slice(0, COLLAPSED_LENGTH)}…` : displayBody}
                  </p>
                  {isLong && (
                    <button
                      type="button"
                      onClick={() => toggleExpanded(item.id)}
                      className="mt-2 text-sm font-medium text-brand underline-offset-4 hover:underline"
                      aria-expanded={!!isExpanded}
                    >
                      {isExpanded ? 'Show less' : 'Show the full message'}
                    </button>
                  )}
                </div>
              )}

              {!d.isOutgoing && item.attachments?.length > 0 && (
                <p className="mt-3 text-sm text-ink-55">
                  {item.attachments.length} attachment{item.attachments.length !== 1 ? 's' : ''}, saved to your Documents.
                </p>
              )}

              {!d.isOutgoing && !d.isSarSent && (
                <div className="mt-3 flex items-center justify-end gap-3 border-t border-rule pt-3">
                  <Button variant="secondary" size="sm" onClick={() => onReply(item)} className="bg-surface">
                    Reply
                  </Button>
                </div>
              )}
            </div>
          );

          // The note that explains a message — pinned to the top of its row
          // in the fixed right-hand band (from `lg` up), out of normal flow
          // so a tall note hangs down over the space below instead of
          // pushing later messages further down the page. Stacks directly
          // under its message, full width, below `lg`.
          const sideNote = hasSideNote && (
            <aside
              className={`mt-3 w-full space-y-2 rounded-lg px-4 py-3 lg:absolute lg:top-0 lg:left-[77%] lg:mt-0 lg:w-[23%] lg:max-w-[320px] ${explainer ? explainerTone : TONE_BOX.needs}`}
              data-testid={explainer ? `explainer-${item.id}` : `id-request-prompt-${item.id}`}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] opacity-70">
                {explainer ? 'What this means' : 'What they need'}
              </p>
              {explainer ? (
                <>
                  <p className="text-[15px]">{explainer.body}</p>
                  {explainer.showHelp && <HelpMeButton sar={sar} onUpdate={onRefresh} />}
                </>
              ) : (
                <>
                  <p className="text-[15px]">
                    {sar?.organisation_name || 'They'} {hasIdDocuments
                      ? "is asking to see proof of who you are — organisations often need to check identity before releasing personal records. You've already uploaded some, so send it now?"
                      : 'is asking to see proof of who you are — organisations often need to check identity before releasing personal records. Upload a photo below and we can send it for you.'}
                  </p>
                  {hasIdDocuments ? (
                    <Button
                      size="sm"
                      onClick={() => onSendId?.(item.id)}
                      disabled={sendingId}
                      data-testid={`send-id-inline-${item.id}`}
                    >
                      Send my ID now
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="bg-surface"
                      onClick={() => onUploadId?.()}
                      data-testid={`upload-id-inline-${item.id}`}
                    >
                      Upload your ID
                    </Button>
                  )}
                </>
              )}
            </aside>
          );

          return (
            <li
              key={item.id}
              className="relative w-full"
              data-testid={`email-${item.direction}-${item.id}`}
            >
              <div className="w-full lg:w-[73%]">
                {bubble}
              </div>
              {sideNote}
            </li>
          );
        })}

        {!sortNewestFirst && waitingBox && <li className="flex justify-center">{waitingBox}</li>}
      </ol>
    </section>
  );
};

export default EmailTimeline;
